import React, { useEffect } from "react";
import { Link, Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ScrollToTop } from "../site/Layout";
import { useAuth } from "../site/auth";
import { IMETA_ICONS, LAONA, LAONA_ICONS, LAONA_NAV, SELFPROTO } from "./data";
import SelfProtoMark from "./SelfProtoMark";
import LaonaHome from "./pages/LaonaHome";
import LaonaWiring from "./pages/LaonaWiring";
import LaonaLibrary from "./pages/LaonaLibrary";
import LaonaPost from "./pages/LaonaPost";
import LaonaEditor from "./pages/LaonaEditor";
import LaonaNotFound from "./pages/LaonaNotFound";
import LaonaRecipes from "./pages/LaonaRecipes";
import LaonaRecipe from "./pages/LaonaRecipe";
import LaonaStart from "./pages/LaonaStart";
import LaonaProjects from "./pages/LaonaProjects";
import LaonaProject from "./pages/LaonaProject";
import LaonaAdminBuilds from "./pages/LaonaAdminBuilds";
import { LaonaLogin, LaonaSignup, RequireMember, RequireVerified } from "./pages/LaonaAuth";
import "./laona.css";

/* =========================================================
   LaonaBot · 라오나봇 (/laonabot/*) — 아이닷메타(I.META)의 시제품 서비스봇
   회원이 원하는 기능을 모듈로 조합해 시제품을 만들도록 돕는다:
   직접 만들기(연결도 도구, 무료) · 라오나봇과 만들기(대화 설계, 무료로 시작 · 한도를 넘으면 이용료).
   글꼴 · 색 · 탭 아이콘은 라오나봇 것을 쓰고, 푸터에 운영사(I.META)를 밝힌다.
   소개 · 레시피 갤러리는 누구나, 프로젝트 · 연결도 · 자료실은 회원 전용
   (서버도 /api/laona/* · /api/forum 에서 막는다). 회원 계정 · 인증 절차는 I.META 와 같다.
   ========================================================= */
const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans+KR:wght@400;500;600;700&display=swap";

function setIcons(list) {
  document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((n) => n.remove());
  for (const attrs of list) {
    const link = document.createElement("link");
    for (const [k, v] of Object.entries(attrs)) link.setAttribute(k, v);
    document.head.appendChild(link);
  }
}

/* 라오나봇 화면에 있는 동안만 문서 전체(배경 · 탭 아이콘 · 글꼴)를 바꾼다 */
function useLaonaDocument() {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("lb-root");
    setIcons(LAONA_ICONS);

    let font = document.getElementById("lb-font");
    if (!font) {
      font = document.createElement("link");
      font.id = "lb-font";
      font.rel = "stylesheet";
      font.href = FONT_HREF;
      document.head.appendChild(font);
    }
    const theme = document.querySelector('meta[name="theme-color"]');
    const prevTheme = theme?.getAttribute("content");
    theme?.setAttribute("content", "#eef1ef");

    return () => {
      root.classList.remove("lb-root");
      setIcons(IMETA_ICONS);
      if (theme && prevTheme) theme.setAttribute("content", prevTheme);
    };
  }, []);
}

export function LaonaMark({ size = 28 }) {
  return (
    <svg className="lb-mark" width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path d="M16 7V3.6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="16" cy="3" r="2.4" fill="currentColor" />
      <rect x="2" y="7" width="28" height="23" rx="6.5" fill="currentColor" />
      <circle cx="10.5" cy="16.5" r="3.1" fill="var(--lb-accent-ink)" />
      <circle cx="21.5" cy="16.5" r="3.1" fill="var(--lb-accent-ink)" />
      <path d="M9 24.5h4.5l2.5-2.5 2.5 2.5H23" fill="none" stroke="var(--lb-accent-ink)"
            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AuthBox() {
  const { member, ready, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (!ready) return <span className="lb-auth-box" />;
  if (!member) {
    return (
      <span className="lb-auth-box">
        <Link to="/laonabot/login" state={{ from: pathname }} className="lb-nav__login">로그인</Link>
      </span>
    );
  }
  return (
    <span className="lb-auth-box">
      <span className="lb-auth-box__name" title={member.email}>{member.name} 님</span>
      <button
        type="button"
        className="lb-nav__login"
        onClick={async () => { await logout().catch(() => {}); navigate("/laonabot"); }}
      >
        로그아웃
      </button>
    </span>
  );
}

function Header() {
  /* 연결도 화면(/laonabot/wiring)은 메뉴에 없다 — 내 프로젝트에서 열므로 그동안 '내 프로젝트'를 켜 둔다 */
  const { pathname, search } = useLocation();
  const { member } = useAuth();
  const onEditor = pathname.startsWith("/laonabot/wiring") && new URLSearchParams(search).get("tab") === "editor";
  const onWiring = pathname.startsWith("/laonabot/wiring") && !onEditor;
  return (
    <header className="lb-nav">
      {/* 운영사 홈페이지로 돌아가는 버튼 — 로고는 이 서비스의 소개(/laonabot)로 가므로 따로 둔다 */}
      <Link to="/" className="lb-nav__home">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <path d="M3 11.5 12 4l9 7.5" />
          <path d="M5.5 9.5V20h13V9.5" />
          <path d="M10 20v-5.5h4V20" />
        </svg>
        <span>아이닷메타홈</span>
      </Link>
      <Link to={LAONA.base} className="lb-brand lb-brand--sp" aria-label={`${SELFPROTO.name} 홈`}>
        <SelfProtoMark ink="var(--lb-accent-ink)" className="lb-mark" />
        <b>{SELFPROTO.lines.map((t) => <i key={t}>{t}</i>)}</b>
      </Link>
      <nav className="lb-nav__links" aria-label="라오나봇 메뉴">
        {LAONA_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `lb-nav__link ${isActive || (onWiring && item.to === "/laonabot/projects") ? "is-active" : ""} ${item.mobileHide ? "lb-nav__link--wide" : ""}`}
          >
            {item.short ? (
              <><span className="lb-nav__full">{item.label}</span><span className="lb-nav__short">{item.short}</span></>
            ) : item.label}
          </NavLink>
        ))}
        {/* 모듈 관리 — 라오나봇 관리자만. 연결도 도구를 모듈 관리 화면으로 연다 (서버가 등록 · 수정 권한을 다시 확인한다) */}
        {member?.laona_admin && (
          <Link to="/laonabot/wiring?tab=editor" className={`lb-nav__link ${onEditor ? "is-active" : ""}`}>
            <span className="lb-nav__full">모듈 관리</span><span className="lb-nav__short">모듈</span>
          </Link>
        )}
      </nav>
      <AuthBox />
    </header>
  );
}

function Footer() {
  return (
    <footer className="lb-footer">
      <div className="lb-footer__inner">
        <div className="lb-footer__brand">
          <LaonaMark size={22} />
          <b>{LAONA.name}</b>
          <span>{LAONA.tagline}</span>
        </div>
        <nav className="lb-footer__links" aria-label="바로 가기">
          {LAONA_NAV.map((item) => <Link key={item.to} to={item.to}>{item.label}</Link>)}
        </nav>
      </div>
      <p className="lb-footer__note">
        라오나봇은 <a href="/">{LAONA.operator}</a>가 운영하는 시제품 서비스입니다.
        카탈로그 밖의 개발 · 양산은 <a href="/contact">개발 문의</a>로 보내 주세요.
        <br />© {new Date().getFullYear()} I.META · LaonaBot
      </p>
    </footer>
  );
}

function Layout() {
  useLaonaDocument();
  const { pathname } = useLocation();
  /* 연결도 화면 · 라오나봇 프로젝트(연결도 + 대화창)는 도구가 화면 전체를 쓰므로 푸터를 숨긴다 */
  const path = pathname.replace(/\/+$/, "");
  const fullBleed = path === "/laonabot/wiring" || /^\/laonabot\/projects\/\d+$/.test(path);
  return (
    <div className={`lb ${fullBleed ? "lb--tool" : ""}`}>
      <ScrollToTop />
      <Header />
      <main className="lb-main">
        <Outlet />
      </main>
      {!fullBleed && <Footer />}
    </div>
  );
}

export default function LaonaApp() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<LaonaHome />} />
        <Route path="login" element={<LaonaLogin />} />
        <Route path="signup" element={<LaonaSignup />} />
        <Route path="recipes" element={<LaonaRecipes />} />
        <Route path="recipes/:id" element={<LaonaRecipe />} /> {/* 회원이 공개한 내 프로젝트 레시피 */}
        <Route path="start" element={<RequireMember><RequireVerified><LaonaStart /></RequireVerified></RequireMember>} />
        <Route path="projects" element={<RequireMember><RequireVerified><LaonaProjects /></RequireVerified></RequireMember>} />
        <Route path="projects/:id" element={<RequireMember><RequireVerified><LaonaProject /></RequireVerified></RequireMember>} />
        <Route path="wiring" element={<RequireMember><RequireVerified><LaonaWiring /></RequireVerified></RequireMember>} />
        <Route path="pinwire.html" element={<Navigate to="/laonabot/wiring" replace />} />
        <Route path="library" element={<RequireMember><LaonaLibrary /></RequireMember>} />
        <Route path="library/:id" element={<RequireMember><LaonaPost /></RequireMember>} />
        {/* 자료 올리기 · 수정 — 라오나봇 관리자만 (서버 /api/laona/library 가 다시 확인한다) */}
        <Route path="library/new" element={<RequireMember><RequireVerified><LaonaEditor /></RequireVerified></RequireMember>} />
        <Route path="library/:id/edit" element={<RequireMember><RequireVerified><LaonaEditor /></RequireVerified></RequireMember>} />
        {/* 빌드 검수 — 라오나봇 관리자만 (서버 /api/admin/* 가 다시 확인한다) */}
        <Route path="admin/builds" element={<RequireMember><RequireVerified><LaonaAdminBuilds /></RequireVerified></RequireMember>} />
        <Route path="*" element={<LaonaNotFound />} />
      </Route>
    </Routes>
  );
}
